

import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormFeedback,
  FormGroup,
  Row,
  Col,
} from "reactstrap";

import Tesseract from "tesseract.js";
import axios from "../../../utills/api"

const ID_REQUIREMENTS = {
  aadhaar: { multiple: true, requiredCount: 2, labels: ["Aadhaar Front", "Aadhaar Back"] },
  dl: { multiple: false, requiredCount: 1, labels: ["Driving License (single image/pdf)"] },
  passport: { multiple: false, requiredCount: 1, labels: ["Passport (single image/pdf)"] },
};

const KycModal = ({ isOpen, toggle, bookingId, onSuccess, }) => {
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [extractedText, setExtractedText] = useState("");
  const [autoFields, setAutoFields] = useState({ name: "", address: "" });

  // 🎉 NEW: success popup state
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIdType("");
      setIdNumber("");
      setFiles([]);
      setErrors({});
      setPreviewUrls([]);
      setUploading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const urls = files.map((f) => {
      if (f && typeof f === "object") return URL.createObjectURL(f);
      return null;
    });
    setPreviewUrls(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [files]);

  const validate = () => {
    const e = {};
    if (!bookingId) e.bookingId = "Booking id missing";
    if (!idType) e.idType = "Please select ID type";
    if (!idNumber || idNumber.trim().length < 3)
      e.idNumber = "Please enter a valid ID number";

    const req = ID_REQUIREMENTS[idType];
    if (req) {
      if (req.requiredCount > 1) {
        if (!files || files.length < req.requiredCount) {
          e.files = `Please upload ${req.requiredCount} files: ${req.labels.join(" & ")}`;
        }
      } else {
        if (!files || files.length < 1) {
          e.files = `Please upload ${req.labels[0]}`;
        }
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileChange = async (e, index = 0) => {
    const req = ID_REQUIREMENTS[idType];
    if (!req) return;

    const file = e.target.files[0];
    if (!file) return;

    if (req.multiple) {
      const newFiles = Array.from({ length: req.requiredCount }).map(
        (_, i) => files[i] || null
      );
      newFiles[index] = file;
      setFiles(newFiles);
    } else {
      setFiles([file]);
    }

    try {
      const { data: { text } } = await Tesseract.recognize(file, "eng+hin");
      const cleanText = text.replace(/\s+/g, " ").trim();

      const idMatch = cleanText.match(/\b\d{4}\s?\d{4}\s?\d{4}\b/);

      const nameMatch =
        cleanText.match(/Name[:\s]*([A-Za-z ]{3,})/i) ||
        cleanText.match(/\b([A-Z][a-z]+\s[A-Z][a-z]+)/);

      let extractedAddress = "";

      const addressPatterns = [
        /Address[:\s]*([\s\S]{20,300})/i,
        /ADDRESS[:\s]*([\s\S]{20,300})/i,
      ];

      for (const pattern of addressPatterns) {
        const match = cleanText.match(pattern);

        if (match?.[1]) {
          extractedAddress = match[1]
            .split("VID")[0]
            .split("UIDAI")[0]
            .trim();

          break;
        }
      }

     setAutoFields({
  name: extractedName,
  address: extractedAddress,
});

      if (idMatch) setIdNumber(idMatch[0].replace(/\s/g, ""));
    } catch (err) {
      console.error("OCR error:", err);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setUploading(true);
      const tenantId = localStorage.getItem("tenantId");

      const form = new FormData();
      form.append("tenantId", tenantId);
      form.append("bookingId", bookingId);
      form.append("idType", idType);
      form.append("idNumber", idNumber);
      form.append("extractedName", autoFields.name);
      form.append("extractedAddress", autoFields.address);

      if (Array.isArray(files)) {
        files.forEach((f) => f && form.append("documents", f));
      } else if (files) {
        form.append("documents", files);
      }

      const res = await axios.post("/kyc", form, {
        withCredentials: true,               // ✅ Added here
        headers: { "Content-Type": "multipart/form-data" },  // needed for FormData
      });

      if (res.status === 200 || res.status === 201) {
        setUploading(false);

        // Update parent immediately
        if (onSuccess) {
          onSuccess(res.data);
        }

        toggle();

        setShowSuccessModal(true);
      } else {
        console.error("KYC upload failed", res.data);
        alert("Failed to upload KYC.");
        setUploading(false);
      }
    } catch (err) {
      console.error(err);
      alert("Upload error (check console)");
      setUploading(false);
    }
  };

  useEffect(() => {
    console.log(
      "KYC Modal BookingId:",
      bookingId
    );
  }, [bookingId]);

  const renderFileInputs = () => {
    if (!idType) return null;
    const req = ID_REQUIREMENTS[idType];
    if (!req) return null;

    if (req.multiple) {
      return req.labels.map((lbl, idx) => (
        <FormGroup key={idx} className="mb-2">
          <Label className="form-label">{lbl}</Label>
          <Input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, idx)}
          />
        </FormGroup>
      ));
    }

    return (
      <FormGroup className="mb-2">
        <Label className="form-label">{req.labels[0]}</Label>
        <Input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => handleFileChange(e)}
        />
      </FormGroup>
    );
  };

  return (
    <>
      {/* ========================== */}
      {/*   MAIN KYC UPLOAD MODAL   */}
      {/* ========================== */}
      <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Update KYC</ModalHeader>

        <ModalBody>
          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label className="form-label">Select ID Type</Label>
                <Input
                  type="select"
                  value={idType}
                  onChange={(e) => {
                    setIdType(e.target.value);
                    setFiles([]);
                    setErrors((p) => ({ ...p, files: undefined }));
                  }}
                >
                  <option value="">-- Select --</option>
                  <option value="aadhaar">Aadhaar</option>
                  <option value="dl">Driving License</option>
                  <option value="passport">Passport</option>
                </Input>
                {errors.idType && (
                  <FormFeedback style={{ display: "block" }}>
                    {errors.idType}
                  </FormFeedback>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label className="form-label">ID Number</Label>
                <Input
                  type="text"
                  placeholder="Enter ID Number"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                />
                {errors.idNumber && (
                  <FormFeedback style={{ display: "block" }}>
                    {errors.idNumber}
                  </FormFeedback>
                )}
              </FormGroup>
            </Col>

            <Col md={12}>
              {renderFileInputs()}
              {errors.files && (
                <div className="text-danger small">{errors.files}</div>
              )}
            </Col>

            {previewUrls.length > 0 && (
              <Col md={12}>
                <div className="d-flex gap-2 flex-wrap">
                  {previewUrls.map((u, i) =>
                    u ? (
                      <div key={i} style={{ width: 120, textAlign: "center" }}>
                        <img
                          src={u}
                          alt={`preview-${i}`}
                          style={{
                            width: "100%",
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />
                        <div className="small text-truncate">
                          {files[i]?.name}
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
              </Col>
            )}
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={uploading}>
            Cancel
          </Button>
          <Button color="primary" disabled={uploading} onClick={handleSubmit}>
            {uploading ? "Uploading..." : "Submit KYC"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ============================= */}
      {/* 🎉 SUCCESS POPUP (Cart style) */}
      {/* ============================= */}
      <Modal isOpen={showSuccessModal} centered>
        <ModalBody className="text-center">
          <h4 className="mb-3 text-success">🎉 KYC Uploaded Successfully!</h4>
          <p>Your documents have been submitted for verification.</p>
        </ModalBody>

        <ModalFooter className="justify-content-center">
          <Button
            color="primary"
            onClick={() => setShowSuccessModal(false)}
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default KycModal;
